import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { ShieldCheck, Sparkles, Zap, X, Check, AlertTriangle } from 'lucide-react';
export function HeroSection() {
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative min-h-screen flex items-center pt-20 pb-16 px-5 overflow-hidden" data-id="element-75">
      {/* Background Effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#00d4ff]/10 rounded-full blur-[120px] pointer-events-none" data-id="element-76" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#7c3aed]/10 rounded-full blur-[120px] pointer-events-none" data-id="element-77" />

      <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-2 gap-12 items-center relative z-10" data-id="element-78">
        {/* Text Content */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="flex flex-col gap-6" data-id="element-79">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 w-fit" data-id="element-80">
            <Sparkles className="w-4 h-4 text-[#00d4ff]" data-id="element-81" />
            <span className="text-sm font-medium text-gray-300" data-id="element-82">
              El Método Operativo Definitivo
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight" data-id="element-83">
            Deja de Jugar a la{' '}
            <span className="text-gradient-cyan" data-id="element-84">"Lotería de Prompts"</span> y
            Construye una Identidad Virtual Inquebrantable.
          </h1>

          <p className="text-lg md:text-xl text-gray-400 leading-relaxed max-w-xl" data-id="element-85">
            El sistema exacto para crear Clones IA Hiperrealistas con
            consistencia total, sin que el rostro cambie en cada generación.
          </p>

          <div className="pt-4 flex flex-col sm:flex-row gap-4" data-id="element-86">
            <Button onClick={scrollToPricing} fullWidth className="sm:w-auto" data-id="element-87">
              QUIERO EL MÉTODO DEFINITIVO
              <Zap className="w-5 h-5 ml-1" data-id="element-88" />
            </Button>
          </div>

          <div className="flex items-center gap-3 pt-4" data-id="element-89">
            <div className="flex -space-x-2" data-id="element-90">
              {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-[#0a0a0f] bg-gray-800 flex items-center justify-center overflow-hidden" data-id="element-91">
                  <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900" data-id="element-92" />
                </div>)}
            </div>
            <div className="flex flex-col" data-id="element-93">
              <div className="flex items-center gap-1" data-id="element-94">
                {[1, 2, 3, 4, 5].map(i => <svg key={i} className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20" data-id="element-95">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-96" />
                  </svg>)}
              </div>
              <span className="text-xs text-gray-400" data-id="element-97">
                Utilizado por agencias top.
              </span>
            </div>
          </div>
        </motion.div>

        {/* Visual Comparison */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="relative w-full rounded-2xl overflow-hidden glass-card" data-id="element-98">
          {/* BEFORE - Sin Sistema */}
          <div className="p-5 md:p-6 border-b border-white/10" data-id="element-99">
            <div className="flex items-center gap-2 mb-4" data-id="element-100">
              <AlertTriangle className="w-4 h-4 text-red-400" data-id="element-101" />
              <span className="text-red-400 text-sm font-bold uppercase tracking-wider" data-id="element-102">
                Sin Sistema — Lotería de Prompts
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2 md:gap-3" data-id="element-103">
              {/* 4 different face silhouettes — each one a DIFFERENT person */}
              {[{
              // Round face, small nose
              head: 'M50,15 C72,15 85,30 85,52 C85,65 80,78 72,86 C68,90 62,95 50,95 C38,95 32,90 28,86 C20,78 15,65 15,52 C15,30 28,15 50,15Z',
              nose: 'M50,48 L47,62 L53,62Z',
              eyeL: {
                cx: 38,
                cy: 42
              },
              eyeR: {
                cx: 62,
                cy: 42
              },
              mouth: 'M40,72 Q50,78 60,72',
              bg: 'from-red-900/30 to-gray-900'
            }, {
              // Long face, prominent nose
              head: 'M50,10 C70,10 80,28 80,48 C80,65 78,82 70,90 C65,95 58,100 50,100 C42,100 35,95 30,90 C22,82 20,65 20,48 C20,28 30,10 50,10Z',
              nose: 'M50,44 L44,64 L56,64Z',
              eyeL: {
                cx: 36,
                cy: 38
              },
              eyeR: {
                cx: 64,
                cy: 38
              },
              mouth: 'M38,76 Q50,82 62,76',
              bg: 'from-orange-900/30 to-gray-900'
            }, {
              // Square jaw face
              head: 'M50,12 C72,12 82,28 82,50 C82,68 80,80 75,88 C70,94 62,98 50,98 C38,98 30,94 25,88 C20,80 18,68 18,50 C18,28 28,12 50,12Z',
              nose: 'M50,46 L46,58 L54,58Z',
              eyeL: {
                cx: 36,
                cy: 40
              },
              eyeR: {
                cx: 64,
                cy: 40
              },
              mouth: 'M38,70 Q50,76 62,70',
              bg: 'from-gray-700 to-red-900/20'
            }, {
              // Heart-shaped face, wide forehead
              head: 'M50,12 C76,12 88,30 86,50 C84,66 76,80 66,90 C60,96 55,98 50,98 C45,98 40,96 34,90 C24,80 16,66 14,50 C12,30 24,12 50,12Z',
              nose: 'M50,46 L45,60 L55,60Z',
              eyeL: {
                cx: 35,
                cy: 40
              },
              eyeR: {
                cx: 65,
                cy: 40
              },
              mouth: 'M40,72 Q50,78 60,72',
              bg: 'from-yellow-900/20 to-gray-800'
            }].map((face, i) => <div key={i} className="relative aspect-[3/4] rounded-lg overflow-hidden" data-id="element-104">
                  <div className={`absolute inset-0 bg-gradient-to-b ${face.bg}`} data-id="element-105" />
                  {/* Face SVG */}
                  <svg viewBox="0 0 100 110" className="absolute inset-0 w-full h-full p-2 md:p-3 opacity-60" data-id="element-106">
                    <path d={face.head} fill="none" stroke="rgba(239,68,68,0.4)" strokeWidth="1.5" data-id="element-107" />
                    <path d={face.nose} fill="none" stroke="rgba(239,68,68,0.3)" strokeWidth="1" data-id="element-108" />
                    <circle cx={face.eyeL.cx} cy={face.eyeL.cy} r="3" fill="rgba(239,68,68,0.3)" data-id="element-109" />
                    <circle cx={face.eyeR.cx} cy={face.eyeR.cy} r="3" fill="rgba(239,68,68,0.3)" data-id="element-110" />
                    <path d={face.mouth} fill="none" stroke="rgba(239,68,68,0.25)" strokeWidth="1" data-id="element-111" />
                  </svg>
                  {/* Glitch line */}
                  <div className="absolute top-[40%] left-0 w-full h-[2px] bg-red-500/40" data-id="element-112" />
                  {/* X mark */}
                  <div className="absolute bottom-2 right-2" data-id="element-113">
                    <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center" data-id="element-114">
                      <X className="w-3 h-3 md:w-3.5 md:h-3.5 text-red-400" data-id="element-115" />
                    </div>
                  </div>
                  <div className="absolute inset-0 rounded-lg border border-red-500/20" data-id="element-116" />
                </div>)}
            </div>
            <p className="text-red-400/70 text-xs mt-3 text-center" data-id="element-117">
              Cada generación = un rostro diferente
            </p>
          </div>

          {/* AFTER - Con el Método */}
          <div className="p-5 md:p-6 relative" data-id="element-118">
            <div className="absolute inset-0 bg-[#00d4ff]/[0.02] pointer-events-none" data-id="element-119" />
            <div className="flex items-center gap-2 mb-4 relative z-10" data-id="element-120">
              <ShieldCheck className="w-4 h-4 text-[#00d4ff]" data-id="element-121" />
              <span className="text-[#00d4ff] text-sm font-bold uppercase tracking-wider" data-id="element-122">
                Con el Método — Identidad Anclada
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2 md:gap-3 relative z-10" data-id="element-123">
              {/* 4 IDENTICAL face silhouettes — same person, different scenarios */}
              {[{
              bg: 'from-gray-800 to-gray-900',
              label: 'Estudio'
            }, {
              bg: 'from-blue-900/20 to-gray-900',
              label: 'Exterior'
            }, {
              bg: 'from-amber-900/10 to-gray-900',
              label: 'Golden Hour'
            }, {
              bg: 'from-purple-900/15 to-gray-900',
              label: 'Noche'
            }].map((scenario, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5 + i * 0.1
            }} className="relative aspect-[3/4] rounded-lg overflow-hidden" data-id="element-124">
                  <div className={`absolute inset-0 bg-gradient-to-b ${scenario.bg}`} data-id="element-125" />
                  {/* Same exact face on all 4 */}
                  <svg viewBox="0 0 100 110" className="absolute inset-0 w-full h-full p-2 md:p-3 opacity-70" data-id="element-126">
                    <path d="M50,14 C70,14 82,30 82,50 C82,65 78,78 70,86 C64,92 58,96 50,96 C42,96 36,92 30,86 C22,78 18,65 18,50 C18,30 30,14 50,14Z" fill="none" stroke="rgba(0,212,255,0.4)" strokeWidth="1.5" data-id="element-127" />
                    <path d="M50,46 L46,60 L54,60Z" fill="none" stroke="rgba(0,212,255,0.35)" strokeWidth="1" data-id="element-128" />
                    <circle cx="37" cy="40" r="3" fill="rgba(0,212,255,0.35)" data-id="element-129" />
                    <circle cx="63" cy="40" r="3" fill="rgba(0,212,255,0.35)" data-id="element-130" />
                    <path d="M40,72 Q50,78 60,72" fill="none" stroke="rgba(0,212,255,0.3)" strokeWidth="1" data-id="element-131" />
                    {/* Hair hint */}
                    <path d="M24,36 C28,18 38,12 50,12 C62,12 72,18 76,36" fill="none" stroke="rgba(0,212,255,0.2)" strokeWidth="1.5" data-id="element-132" />
                  </svg>
                  {/* Scenario label */}
                  <div className="absolute top-2 left-2" data-id="element-133">
                    <span className="text-[8px] md:text-[9px] text-[#00d4ff]/50 font-medium uppercase tracking-wider" data-id="element-134">
                      {scenario.label}
                    </span>
                  </div>
                  {/* Check mark */}
                  <div className="absolute bottom-2 right-2" data-id="element-135">
                    <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-[#00d4ff]/20 border border-[#00d4ff]/40 flex items-center justify-center" data-id="element-136">
                      <Check className="w-3 h-3 md:w-3.5 md:h-3.5 text-[#00d4ff]" data-id="element-137" />
                    </div>
                  </div>
                  <div className="absolute inset-0 rounded-lg border border-[#00d4ff]/20" data-id="element-138" />
                </motion.div>)}
            </div>
            <p className="text-[#00d4ff]/70 text-xs mt-3 text-center relative z-10" data-id="element-139">
              Mismo rostro, infinitos escenarios
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}